<template>
  <div class="p-3 pt-4">
    <transition name="fade">
      <MenssageAlert :messages="messages" />
    </transition>
    <div v-if="isLoadingCustomFields || isLoadingSenderDomains" class="d-flex justify-content-center align-items-center">
      <CSpinner class="my-3" color="primary" />
    </div>
    <div v-else>
      <AutomationImageTemplate v-if="templateId" :template="imageTemplate" @removeTemplate="removeTemplate" />
      <CRow v-else class="pb-3 justify-content-between align-items-center border-bottom">
        <CCol sm="4">
          <CButton
            @click="redirectToEmailTemplates"
            tag="button"
            class="btn bt-white w-100 btn-choose text-uppercase font-xs p-2 d-flex justify-content-center align-items-center"
          >
            <CIcon name="plus" :height="24" viewBox="0 0 32 32" />
            <p class="mb-0 text-uppercase ml-2" v-if="templateId">
              {{ $t('automation.email.content.template.btn.change') }}
            </p>
            <p class="mb-0 text-uppercase ml-2" v-else>
              {{ $t('automation.email.content.template.btn.choose') }}
            </p>
          </CButton>
        </CCol>
        <CCol sm="8" class="text-right">
          <CSpinner v-if="isLoadingAvailableTemplates" color="primary" style="width: 1.5rem; height: 1.5rem" />
          <span v-else class="text-gray-600"> {{ availableTemplatesCounter || '-' }} {{ $t('automation.email.content.template.available') }} </span>
        </CCol>
      </CRow>
      <div class="mt-3">
        <label class="font-weight-bold mb-2">{{ $t('automation.email.content.select-5.value') }}</label>
        <label for="CustomFields" class="d-flex justify-content-between">
          <span class="text-gray-600">{{ $t('automation.email.content.select-1.value') }}</span>
          <span style="font-size: 14px" class="text-gray-600">{{ $t('automation.email.content.select-1.observation') }}</span>
        </label>
        <multiselect
          class="no-close border-gray-300 rounded select mb-3"
          v-model="customFieldSelect"
          custom
          :multiple="false"
          :loading="isLoadingCustomFields"
          :disabled="isLoadingCustomFields"
          :custom-label="(option) => option.value"
          :options="customFieldsSelectOptions || []"
          :allow-empty="false"
          :placeholder="$t(`automation.email.content.select.one`)"
        />
        <CButton :disabled="!customFieldSelect" color="primary" class="px-4 py-2 table-header-button align-middle" @click="addCustomField()">
          {{ $t('automation.email.content.select-1.action') }}
        </CButton>
        <textarea
          ref="subjectRef"
          class="form-control mt-3"
          rows="4"
          maxlength="100"
          style="resize: none"
          v-model="subject"
          :maxlength="subjectMaxLength"
          @keyup="updateField('subject', subject)"
        />
      </div>
      <div class="mt-3">
        <label for="DisplayName" class="d-flex justify-content-between">
          <span class="font-weight-bold">{{ $t('automation.email.content.select-2.value') }}</span>
        </label>
        <CInput
          @keyup="updateField('displayName', displayName)"
          v-model="displayName"
          :placeholder="$t('automation.email.content.select-2.placeholder')"
          class="form-group--line border-gray-300 rounded"
          maxlength="50"
        />
      </div>
      <div class="mt-3">
        <label for="From" class="d-flex justify-content-between">
          <span class="font-weight-bold">{{ $t('automation.email.content.select-3.value') }}</span>
        </label>
        <CInput
          @keyup="updateField('from', from)"
          @keypress="isAlphaNum($event)"
          @paste="isAlphaNum($event)"
          v-model="from"
          :placeholder="$t('automation.email.content.select-3.placeholder')"
          class="form-group--line border-gray-300 rounded"
          maxlength="50"
        />
        <multiselect
          class="no-close border-gray-300 rounded select mb-3"
          v-model="senderDomain"
          custom
          :multiple="false"
          :loading="isLoadingSenderDomains"
          :disabled="isLoadingSenderDomains"
          :custom-label="(option) => option.label"
          :options="senderDomainOptions || []"
          :allow-empty="false"
          :placeholder="$t(`automation.email.content.select.one`)"
          @input="updateField('senderId', senderDomain.value)"
        />
      </div>
      <div class="mt-3">
        <label for="Reply" class="d-flex justify-content-between">
          <span class="font-weight-bold">{{ $t('automation.email.content.select-4.value') }}</span>
          <span style="font-size: 14px" class="text-gray-600">{{ $t('automation.email.content.select-4.observation') }}</span>
        </label>
        <CInput
          @keyup="updateField('replyTo', replyTo)"
          v-model.trim="$v.replyTo.$model"
          :placeholder="$t('automation.email.content.select-4.placeholder')"
          maxlength="50"
          class="form-group--line border-gray-300 rounded"
          :isValid="checkIfValid('replyTo')"
          :invalidFeedback="$t('automation.email.content.select-4.invalid.feedback')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import services from '@/services.js'
import { getSchema } from '@/utils/audienceSchema'
import { getErrorMessage } from '@/utils/errorHandling.js'
import { validationMixin } from 'vuelidate'
import { email } from 'vuelidate/lib/validators'
import AutomationImageTemplate from './AutomationImageTemplate.vue'
import MenssageAlert from '@/components/MenssageAlert'
import { DYNAMIC_TEMPLATE_DESTINATION_TYPE } from '@/views/template/constants.js'
import { FIELD_TYPE } from '../constants'

export default {
  name: 'AutomationEmailContent',
  components: { AutomationImageTemplate, MenssageAlert },
  props: {
    indexStep: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      messages: [],
      customFieldSelect: undefined,
      customFieldsSelectOptions: undefined,
      isLoadingCustomFields: false,
      from: undefined,
      senderDomain: undefined,
      senderDomainOptions: undefined,
      isLoadingSenderDomains: false,
      isLoadingAvailableTemplates: false,
      availableTemplatesCounter: undefined,
      subject: undefined,
      subjectMaxLength: 100,
      displayName: undefined,
      replyTo: undefined,
      templateName: undefined,
      thumbnailUri: undefined,
      templateId: undefined,
      contentType: undefined,
      DYNAMIC_TEMPLATE_DESTINATION_TYPE: DYNAMIC_TEMPLATE_DESTINATION_TYPE,
      templateData: undefined,
    }
  },
  mixins: [validationMixin],
  validations: {
    replyTo: {
      email,
    },
  },
  async mounted() {
    this.loadCustomFields()
    this.loadAvailableTemplates()
    await this.loadSenderDomains()
    this.loadData()
  },
  methods: {
    isAlphaNum(event) {
      var regex = new RegExp('^[a-zA-Z0-9._+,\\-]+$')
      var key = String.fromCharCode(!event.charCode ? event.which : event.charCode)
      if (!regex.test(key)) {
        event.preventDefault()
        return false
      }
    },
    updateField(name, value, type = FIELD_TYPE.STEP, formatter) {
      this.$nextTick(() => {
        if (typeof formatter == 'function') {
          value = formatter()
        }

        const obj = {
          property: name,
          value: value,
          type: type,
        }
        this.$emit('update-field', obj)
        const isFormDirtyAndInvalid = this.$v.$dirty && this.$v.$invalid
        this.$emit('update-form-validation', isFormDirtyAndInvalid)
      })
    },
    loadData() {
      const workflowJsonData = this.$store.state.automation.mainWorkflow.workflowJson.steps.values[0]?.data

      this.templateId = workflowJsonData?.templateId
      this.from = workflowJsonData?.from
      this.displayName = workflowJsonData?.displayName
      this.contentType = workflowJsonData?.contentType
      this.subject = workflowJsonData?.subject
      this.replyTo = workflowJsonData?.replyTo
      this.senderId = workflowJsonData?.senderId
      this.senderDomain = this.senderDomainOptions?.find((senderDomain) => senderDomain.value == this.senderId)

      if (this.templateId) {
        this.loadTemplateData()
      }
    },
    async loadAvailableTemplates() {
      try {
        this.isLoadingAvailableTemplates = true
        const organizationId = this.$route.params.organizationId
        const params = { page: 1, count: 1, sort: 2, SortField: 'updatedDateTime' }

        const response = await services.templatesEmail(organizationId, { params: params })
        this.availableTemplatesCounter = response.data.data.pagination.totalRecords
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingAvailableTemplates = false
      }
    },
    async loadCustomFields() {
      try {
        this.isLoadingCustomFields = true
        const organizationId = this.$route.params.organizationId

        this.customFieldsSelectOptions = await getSchema(organizationId)
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingCustomFields = false
      }
    },
    async loadSenderDomains() {
      try {
        this.isLoadingSenderDomains = true
        let organizationId = this.$route.params.organizationId

        const response = await services.getSendersDomainByOrganizationUserId(organizationId)
        let list = response.data.data.map((elm) => {
          return { value: elm.senderId, label: '@' + elm.sender }
        })

        this.senderDomainOptions = list
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingSenderDomains = false
      }
    },
    addCustomField() {
      this.$refs.subjectRef.focus()

      if (!this.subject) {
        this.subject = '[' + this.customFieldSelect.personalizationTag + ']'
      } else {
        this.subject += '[' + this.customFieldSelect.personalizationTag + ']'
      }
      this.updateField('subject', this.subject)
    },
    redirectToEmailTemplates() {
      let { workflowId } = this.$route.params
      this.$router.push({
        name: 'new-template-list',
        params: {
          dynamicId: workflowId,
          dynamicIndex: this.indexStep,
          dynamicDestinationType: DYNAMIC_TEMPLATE_DESTINATION_TYPE.WORKFLOW,
        },
        hash: this.$route.hash,
      })
    },
    //   this.$v.$touch()
    //   if (this.$v.$invalid) {
    //     return
    //   } else {
    //     let model = {
    //       indexStep: this.indexStep,
    //       content: {
    //         from: this.emailPrefix + this.senderDomain?.label || '',
    //         displayName: this.displayName || '',
    //         templateId: this.templateId || '',
    //         contentType: parseInt(this.contentType) || '',
    //         subject: this.subject || '',
    //         replyTo: this.replyTo || '',
    //         thumbnailUri: this.thumbnailUri || '',
    //         templateName: this.templateName || '',
    //         emailSimpleText: this.emailSimpleText || '',
    //         senderId: this.senderDomain?.value || '',
    //         senderName: this.senderDomain?.label || '',
    //         emailPrefix: this.emailPrefix || '',
    //       },
    //     }
    //     this.$store.dispatch('automation/setEmailContent', JSON.parse(JSON.stringify(model)))
    //   }
    // },
    checkIfValid(fieldName) {
      const field = this.$v[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || null)
    },
    async loadTemplateData() {
      const organizationId = this.$route.params.organizationId
      const templateResponse = await services.getTemplate(organizationId, this.templateId)
      this.templateData = templateResponse.data.data
    },
    removeTemplate() {
      this.templateId = null
      this.templateData = null

      this.updateField('templateId', null)
      this.updateField('templateEmail', null, FIELD_TYPE.CONTROL)
    },
  },
  computed: {
    mainWorkflowContent() {
      return this.$store.state.automation.controls.templateEmail || this.templateData
    },
    imageTemplate() {
      return {
        templateName: this.mainWorkflowContent?.name,
        createdDateTime: this.mainWorkflowContent?.createdDateTime,
        thumbnailUri: this.mainWorkflowContent?.thumbnailUri,
        templateId: this.mainWorkflowContent?.id,
        contentType: this.mainWorkflowContent?.contentType,
        indexStep: this.indexStep,
        isUnlayer: this.mainWorkflowContent?.isUnlayer,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-choose {
  border: 1px solid var(--gray-500) !important;

  &:hover {
    border: 1px solid var(--primary) !important;
    color: var(--black) !important;

    .c-icon {
      color: var(--primary) !important;
    }
  }
}
</style>
